import type { TaskListDto, TaskStatusDto } from '@werft/contracts';
import { keyBetween } from '@werft/domain';
import { t } from '../../i18n/index.js';
import { ApiError, newOpId } from '../../platform/api.js';
import { keys, queryClient } from '../../platform/queries.js';
import { upsertList, upsertStatus } from '../../platform/realtime.js';
import { outbox } from '../../platform/sync.js';
import { toast } from '../../platform/toast.js';

/**
 * Struktur eines Projekts (Paket P4): Listen und Board-Spalten. Alles über den Ausgangskorb (ADR 0007); Listen
 * erscheinen sofort als Overlay, Spalten nach Bestätigung (selten geändert, kein Offline-Bedarf).
 */

export const listName = (l: Pick<TaskListDto, 'name'>) => l.name ?? t('list.default');

const failed = (err: unknown) => {
  toast(t('sync.failed', { message: err instanceof Error ? err.message : t('task.failed') }), { kind: 'error' });
};

const reloadStructure = (projectId: string) => void queryClient.invalidateQueries({ queryKey: keys.project(projectId) });

outbox.register('list.create', { done: (e, r) => upsertList(e.projectId, r as TaskListDto), fail: (_e, err) => failed(err) });
outbox.register('list.update', {
  done: (e, r) => upsertList(e.projectId, r as TaskListDto),
  fail: (e, err) => {
    const current = err instanceof ApiError && err.code === 'version_conflict' ? (err.body.current as TaskListDto) : null;
    // Umbenennen/Verschieben ist eine Absicht: auf den neuen Stand setzen und erneut senden (höchstens dreimal)
    const meta = (e.meta ?? {}) as { rebases?: number };
    if (current && (meta.rebases ?? 0) < 3) return { opId: newOpId(), body: { ...(e.body as object), expectedVersion: current.version }, meta: { rebases: (meta.rebases ?? 0) + 1 } };
    if (current) upsertList(e.projectId, current);
    failed(err);
  },
});
outbox.register('list.merge', {
  done: (e, r) => {
    reloadStructure(e.projectId);
    const m = e.meta as { from: string; to: string };
    toast(t('list.merged', m));
    void r;
  },
  fail: (e, err) => {
    reloadStructure(e.projectId);
    failed(err);
  },
});
outbox.register('status.create', { done: (e, r) => upsertStatus(e.projectId, r as TaskStatusDto), fail: (_e, err) => failed(err) });
outbox.register('status.update', {
  done: (e, r) => upsertStatus(e.projectId, r as TaskStatusDto),
  fail: (e, err) => {
    if (err instanceof ApiError && err.code === 'version_conflict') upsertStatus(e.projectId, err.body.current as TaskStatusDto);
    failed(err);
  },
});
outbox.register('status.remove', {
  done: (e, r) => {
    reloadStructure(e.projectId);
    // W-R05: Teilverarbeitung ehrlich melden – die Spalte bleibt mit den übrigen Aufgaben
    const x = r as { moved: number; kept: number; removed: boolean } | undefined;
    if (x && !x.removed) toast(t('removeCol.partialDone', { moved: x.moved, kept: x.kept }), { ms: 10_000 });
  },
  fail: (e, err) => (reloadStructure(e.projectId), failed(err)),
});

/**
 * Liste anlegen. Mit `id` (vom Browser) können direkt danach Aufgaben in diese Liste folgen – der Ausgangskorb sendet
 * streng nacheinander (Audit F04: erste Aufgabe in einem leeren Bereich).
 */
export function createList(projectId: string, name: string, lists: TaskListDto[], areaId: string | null = null, id?: string): Promise<unknown> {
  const opId = newOpId();
  const list: TaskListDto = { id: id ?? `tmp-list-${opId}`, projectId, name, position: keyBetween(lists.at(-1)?.position ?? null, null), areaId, version: 0 };
  return outbox
    .enqueue({
      opId,
      kind: 'list.create',
      projectId,
      method: 'POST',
      url: `/api/projects/${projectId}/lists`,
      body: { opId, name, ...(id ? { id } : {}), ...(areaId ? { areaId } : {}) },
      overlay: { type: 'listCreate', list },
    })
    .catch(() => {});
}

export function renameList(list: TaskListDto, name: string) {
  const opId = newOpId();
  void outbox
    .enqueue({
      opId,
      kind: 'list.update',
      projectId: list.projectId,
      method: 'PATCH',
      url: `/api/lists/${list.id}`,
      body: { opId, expectedVersion: list.version, patch: { name } },
      overlay: { type: 'listPatch', listId: list.id, fields: { name } },
    })
    .catch(() => {});
}

/** Liste an eine Stelle verschieben (Menü „nach oben/unten“) */
export function moveList(list: TaskListDto, ordered: TaskListDto[], to: number) {
  const others = ordered.filter((l) => l.id !== list.id);
  const prev = others[to - 1] ?? null;
  const next = others[to] ?? null;
  let position = list.position;
  try {
    position = keyBetween(prev?.position ?? null, next?.position ?? null);
  } catch {
    /* Server rechnet neu */
  }
  const opId = newOpId();
  void outbox
    .enqueue({
      opId,
      kind: 'list.update',
      projectId: list.projectId,
      method: 'POST',
      url: `/api/lists/${list.id}/move`,
      body: { opId, expectedVersion: list.version, ...(prev ? { afterId: prev.id } : next ? { beforeId: next.id } : {}) },
      overlay: { type: 'listPatch', listId: list.id, fields: { position } },
    })
    .catch(() => {});
}

export function mergeList(source: TaskListDto, target: TaskListDto) {
  const opId = newOpId();
  void outbox
    .enqueue({
      opId,
      kind: 'list.merge',
      projectId: source.projectId,
      method: 'POST',
      url: `/api/lists/${source.id}/merge`,
      body: { opId, targetListId: target.id },
      overlay: { type: 'listMerge', sourceId: source.id, targetId: target.id },
      meta: { from: listName(source), to: listName(target) },
    })
    .catch(() => {});
}

export function createStatus(projectId: string, name: string) {
  const opId = newOpId();
  void outbox.enqueue({ opId, kind: 'status.create', projectId, method: 'POST', url: `/api/projects/${projectId}/statuses`, body: { opId, name } }).catch(() => {});
}

export function renameStatus(projectId: string, s: TaskStatusDto, name: string) {
  const opId = newOpId();
  queryClient.setQueryData(keys.snapshot(projectId), (old: { statuses: TaskStatusDto[] } | undefined) =>
    old ? { ...old, statuses: old.statuses.map((x) => (x.id === s.id ? { ...x, name } : x)) } : old,
  );
  void outbox
    .enqueue({ opId, kind: 'status.update', projectId, method: 'PATCH', url: `/api/statuses/${s.id}`, body: { opId, expectedVersion: s.version, patch: { name } } })
    .catch(() => {});
}

export function removeStatus(projectId: string, s: TaskStatusDto, target: TaskStatusDto, opts: { mode?: 'all' | 'allowed'; override?: { reason: string }; expectedPlan?: string } = {}) {
  const opId = newOpId();
  void outbox
    .enqueue({ opId, kind: 'status.remove', projectId, method: 'POST', url: `/api/statuses/${s.id}/remove`, body: { opId, targetStatusId: target.id, ...opts } })
    .catch(() => {});
}

/* ---------- P9: Spalten sortieren, WIP-Limit, Werkzeuge, Status ---------- */

outbox.register('status.move', {
  done: (e, r) => upsertStatus(e.projectId, r as TaskStatusDto),
  fail: (e, err) => {
    // Verschieben ist eine Absicht: auf den neuen Stand setzen und erneut senden (höchstens dreimal)
    const current = err instanceof ApiError && err.code === 'version_conflict' ? (err.body.current as TaskStatusDto) : null;
    const n = ((e.meta ?? {}) as { rebases?: number }).rebases ?? 0;
    if (current && n < 3) return { opId: newOpId(), body: { ...(e.body as object), expectedVersion: current.version }, meta: { rebases: n + 1 } };
    reloadStructure(e.projectId);
    failed(err);
  },
});

const patchStatusLocally = (projectId: string, id: string, fields: Partial<TaskStatusDto>) =>
  queryClient.setQueryData(keys.snapshot(projectId), (old: { statuses: TaskStatusDto[] } | undefined) =>
    old
      ? { ...old, statuses: old.statuses.map((x) => (x.id === id ? { ...x, ...fields } : x)).sort((a, b) => (a.position < b.position ? -1 : a.position > b.position ? 1 : 0)) }
      : old,
  );

/** Spalte an einen Index verschieben (Menü „nach links/rechts“) */
export function moveStatus(projectId: string, s: TaskStatusDto, ordered: TaskStatusDto[], to: number) {
  const others = ordered.filter((x) => x.id !== s.id);
  const prev = others[to - 1] ?? null;
  const next = others[to] ?? null;
  try {
    patchStatusLocally(projectId, s.id, { position: keyBetween(prev?.position ?? null, next?.position ?? null) });
  } catch {
    /* Server rechnet neu */
  }
  const opId = newOpId();
  void outbox
    .enqueue({
      opId,
      kind: 'status.move',
      projectId,
      method: 'POST',
      url: `/api/statuses/${s.id}/move`,
      body: { opId, expectedVersion: s.version, ...(prev ? { afterId: prev.id } : next ? { beforeId: next.id } : {}) },
    })
    .catch(() => {});
}

/** WIP-Limit setzen oder entfernen (null) – ein Hinweis, keine Sperre */
export function setWipLimit(projectId: string, s: TaskStatusDto, wipLimit: number | null) {
  patchStatusLocally(projectId, s.id, { wipLimit });
  const opId = newOpId();
  void outbox
    .enqueue({ opId, kind: 'status.move', projectId, method: 'PATCH', url: `/api/statuses/${s.id}`, body: { opId, expectedVersion: s.version, patch: { wipLimit } } })
    .catch(() => {});
}
